/**
 * Task #245 — orchestrator hook for the Spotlight quick search palette.
 *
 * Wires the two live tiers (instant local fuzzy + server trigram/keyword).
 * Tier 4 (deep pulse/email content scan) was removed: it was rigid, hung
 * the UI on large databases, and rarely returned hits the LLM-keyword
 * tier didn't already cover. The fast Tier 1 + Tier 2/3 path is now the
 * complete search surface.
 *
 * Maintains a stable sequence id on every keystroke so a slow tier's
 * late response cannot leak past a newer query (sequence mismatch → drop).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { searchLocalIndex, type LocalIndexPayload } from '@/lib/quick-search/local-index';
import { mergeRankedHits } from '@/lib/quick-search/ranking';
import type { SearchHit } from '@/lib/quick-search/types';
import { authedFetch } from '@/lib/authed-fetch';
import { useProjectThumbnailLookup } from '@/lib/quick-search/thumbnail-cache';

const QUICK_DEBOUNCE_MS = 200;

export interface UseQuickSearchResult {
  query: string;
  setQuery: (q: string) => void;
  results: SearchHit[];
  /** Tier 2 server request in flight (for a subtle spinner). */
  isQuickSearching: boolean;
  hasIndex: boolean;
  /**
   * Task #301 — the local index is still loading on a COLD open (no cached
   * data yet). Lets the palette show a "loading" affordance instead of a
   * misleading "no matches" empty state during a backend hiccup.
   */
  isIndexLoading: boolean;
  /**
   * Task #301 — the local index failed to load AND we have no last-known-good
   * cache to fall back on. Distinguishes a genuine "no projects match" from
   * "search data couldn't load" so a pool/backend hiccup never masquerades as
   * a collapsed (one-project) list. Never set while cached data exists —
   * TanStack keeps the previous successful payload, so an in-flight refetch
   * error still renders the full list.
   */
  indexUnavailable: boolean;
  /** Task #301 — manual retry for the local index after a transient failure. */
  retryIndex: () => void;
  /** Task #276 — default rows shown when query is empty. */
  pinnedResults: SearchHit[];
  recentResults: SearchHit[];
}

export function useQuickSearch(enabled: boolean): UseQuickSearchResult {
  const [query, setQuery] = useState('');
  const [serverHits, setServerHits] = useState<SearchHit[]>([]);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  const seqRef = useRef(0);
  const quickAbortRef = useRef<AbortController | null>(null);

  // Tier 1 — fetch the local index once when the palette first opens.
  const indexQuery = useQuery<LocalIndexPayload>({
    queryKey: ['/api/search/index'],
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  // Task #276 — default-state queries (fire only when the palette is
  // open AND the query is empty). Recent comes from the server's
  // per-user interaction history; pinned comes from the user
  // preferences blob already cached by the rest of the app. Pair the
  // staleTime with explicit invalidation in QuickSearchDialog.handleSelect
  // so the just-opened project jumps to the top instantly.
  const showDefaults = enabled && !query.trim();
  const recentQuery = useQuery<{ results: SearchHit[] }>({
    queryKey: ['/api/search/recent'],
    enabled: showDefaults,
    staleTime: 60 * 1000,
  });
  const prefsQuery = useQuery<any>({
    queryKey: ['/api/user/preferences'],
    enabled: showDefaults,
    staleTime: 60 * 1000,
  });

  const localHits = useMemo(() => {
    if (!indexQuery.data) return [];
    if (!query.trim()) return [];
    return searchLocalIndex(indexQuery.data, query, 50);
  }, [indexQuery.data, query]);

  // Reset transient state when query empties.
  useEffect(() => {
    if (!query.trim()) {
      setServerHits([]);
      setIsQuickSearching(false);
      quickAbortRef.current?.abort();
    }
  }, [query]);

  useEffect(() => {
    if (!enabled) return;
    seqRef.current += 1;
    const mySeq = seqRef.current;

    if (!query.trim()) {
      setServerHits([]);
      setIsQuickSearching(false);
      return;
    }

    // Stale-result isolation: every query change clears the Tier 2/3
    // buffer so the user never sees rows that match a previous query.
    // Tier 1 is recomputed synchronously from `localHits` so it does
    // NOT need clearing — its memo already reflects the current query.
    setServerHits([]);

    // Tier 2/3 — debounced server trigram + keyword.
    setIsQuickSearching(true);
    const timer = setTimeout(() => {
      if (mySeq !== seqRef.current) return;
      quickAbortRef.current?.abort();
      const ctrl = new AbortController();
      quickAbortRef.current = ctrl;
      authedFetch(`/api/search/quick?q=${encodeURIComponent(query)}`, { signal: ctrl.signal })
        .then(r => (r.ok ? r.json() : { results: [] }))
        .then((json: { results: SearchHit[] }) => {
          if (mySeq !== seqRef.current) return;
          setServerHits(json.results || []);
          setIsQuickSearching(false);
        })
        .catch((err) => {
          if (mySeq !== seqRef.current) return;
          if (err?.name !== 'AbortError') setIsQuickSearching(false);
        });
    }, QUICK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, enabled]);

  const mergedRaw = useMemo(
    () => mergeRankedHits(localHits, serverHits),
    [localHits, serverHits],
  );

  // Task #252 — overlay project thumbnails read straight from the
  // already-loaded tree-snapshot cache. Zero network cost: misses fall
  // through as `null` and the row renders its existing placeholder.
  const lookupThumbnail = useProjectThumbnailLookup();
  const merged = useMemo(
    () =>
      mergedRaw.map((hit) => {
        if (hit.thumbnailUrl) return hit;
        const cached = lookupThumbnail(hit.projectId);
        return cached ? { ...hit, thumbnailUrl: cached } : hit;
      }),
    [mergedRaw, lookupThumbnail],
  );

  // Task #276 — build default-state groups. Pinned comes from prefs
  // (capped at 10); recent comes from /api/search/recent. Both are
  // mapped through the same thumbnail overlay so the empty-state rows
  // render with thumbnails identical to typed results.
  const pinnedResults = useMemo<SearchHit[]>(() => {
    if (!showDefaults) return [];
    const prefs = prefsQuery.data;
    const pinned = Array.isArray(prefs?.pinnedProjects)
      ? (prefs.pinnedProjects as Array<{ projectId: string; title: string }>)
      : [];
    return pinned.slice(0, 10).map((p) => ({
      id: p.projectId,
      type: 'project' as const,
      projectId: p.projectId,
      projectName: p.title,
      clientId: '',
      clientName: '',
      thumbnailUrl: lookupThumbnail(p.projectId),
      matchReason: {
        field: 'projectName' as const,
        value: p.title,
        matchIndices: [],
      },
      score: 0,
    }));
  }, [showDefaults, prefsQuery.data, lookupThumbnail]);

  const recentResults = useMemo<SearchHit[]>(() => {
    if (!showDefaults) return [];
    const raw = recentQuery.data?.results ?? [];
    return raw.map((hit) => {
      if (hit.thumbnailUrl) return hit;
      const cached = lookupThumbnail(hit.projectId);
      return cached ? { ...hit, thumbnailUrl: cached } : hit;
    });
  }, [showDefaults, recentQuery.data, lookupThumbnail]);

  return {
    query,
    setQuery,
    results: merged,
    isQuickSearching,
    hasIndex: !!indexQuery.data,
    // Cold open, still fetching, nothing cached yet.
    isIndexLoading: enabled && !indexQuery.data && indexQuery.isLoading,
    // Failed AND no last-known-good cache to fall back on.
    indexUnavailable: enabled && !indexQuery.data && indexQuery.isError,
    retryIndex: () => { void indexQuery.refetch(); },
    pinnedResults,
    recentResults,
  };
}
